import React, { useEffect } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import useTheme from "@material-ui/styles/useTheme";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import makeStyles from "@material-ui/styles/makeStyles";
import Typography from "@material-ui/core/Typography";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import useLocals from "@micado-digital/react-ui/hooks/useLocals";
import localDE from "../../../locales/de.json";
import localEN from "../../../locales/en.json";

const useStyles = makeStyles(theme => ({
	root: {
		background: theme?.palette?.background?.footer?.light,
		padding: theme.spacing(5.5, 2.5, 0),
		transform: "translate(0, 48px)",
		[theme.breakpoints.up("lg")]: {
			padding: theme.spacing(15, 2.5, 0),
			transform: "translate(0, 150px)"
		}
	},
	headline: {
		margin: theme.spacing(0, "auto", 2.25),
		maxWidth: 600
	},
	wrapper: {
		display: "grid",
		margin: "0 auto",
		maxHeight: 620,
		maxWidth: 1500,
		textAlign: "center",
		[theme.breakpoints.up("lg")]: {
			gridTemplateColumns: "repeat(16, 1fr)",
			gridTemplateRows: "repeat(16, 1fr)"
		},
		"& > div:not(.mco-footer-02__image) > div": {
			alignItems: "center",
			display: "flex",
			justifyContent: "center"
		}
	},
	left: {
		gridRow: "1 / 2",
		[theme.breakpoints.up("lg")]: {
			gridColumn: "1 / 4",
			textAlign: "right",
			gridRow: "7 / 9",
			"& > div": {
				alignItems: "flex-end !important",
				flexDirection: "column !important"
			}
		},
		[theme.breakpoints.up("xl")]: {
			gridColumn: "1 / 4",
			gridRow: "6 / 9"
		}
	},
	right: {
		gridRow: "2 / 3",
		[theme.breakpoints.up("lg")]: {
			gridColumn: "14 / 17",
			textAlign: "left",
			gridRow: "7 / 9",
			"& > div": {
				alignItems: "flex-start !important",
				flexDirection: "column !important"
			}
		},
		[theme.breakpoints.up("xl")]: {
			gridColumn: "14 / 17",
			gridRow: "6 / 9"
		}
	},
	single: {
		gridRow: "2 / 3",
		[theme.breakpoints.up("lg")]: {
			gridColumn: "13 / 17",
			textAlign: "left",
			gridRow: "7 / 9",
			"& > div": {
				alignItems: "flex-start !important",
				flexDirection: "column !important"
			}
		},
		[theme.breakpoints.up("xl")]: {
			gridColumn: "12 / 16",
			gridRow: "6 / 9"
		}
	},
	name: {
		color: theme.palette.secondary.main,
		fontSize: 14,
		lineHeight: 1.1,
		marginRight: theme.spacing(0.5),
		[theme.breakpoints.up("lg")]: {
			fontFamily: "'Marat-Regular'",
			fontSize: 25
		},
		[theme.breakpoints.up("xl")]: {
			fontSize: 35
		}
	},
	position: {
		fontSize: 14,
		[theme.breakpoints.up("lg")]: {
			fontSize: 16
		}
	},
	image: {
		display: "block",
		margin: theme.spacing(4, -2.5, 0),
		gridRow: "3 / 4",
		maxWidth: 1242,
		[theme.breakpoints.up("sm")]: {
			margin: theme.spacing(4, "auto", 0)
		},
		[theme.breakpoints.up("lg")]: {
			gridColumn: "1 / -1",
			gridRow: "1 / -1"
		},
		"& img": {
			display: "block",
			height: "100%",
			objectFit: "contain",
			width: "100%"
		}
	}
}));

const Team = ({ lang, team }) => {
	const css = useStyles();
	const theme = useTheme();
	const controls = useAnimation();
	const { ref, inView } = useInView();
	const bigScreen = useMediaQuery(theme.breakpoints.up("sm"));
	const l = useLocals({
		lang: lang,
		res: {
			de: localDE,
			en: localEN
		}
	});

	let image = null;
	let type = null;
	let dataLeft = null;
	let dataRight = null;
	let dataSingle = null;

	switch (team?.value) {
		case "Landlinger/Adelsberger":
			dataLeft = {
				name: "Michael Landlinger-Weilbold",
				addition: lang === "de" ? "Junior Chef & Gastgeber" : "Junior Boss & Host"
			};
			dataRight = {
				name: "Evelyn Adelsberger",
				addition: "Marketing"
			};
			type = "duo";
			image = "landlinger-adelsberger";
			break;
		case "Winkler/Landlinger":
			dataLeft = {
				name: "Barbara Winkler",
				addition: lang === "de" ? "Gastgeberin" : "Hostess"
			};
			dataRight = {
				name: "Michael Landlinger-Weilbold",
				addition: lang === "de" ? "Junior Chef & Gastgeber" : "Junior Boss & Host"
			};
			type = "duo";
			image = "winkler-landlinger";
			break;
		// case "Krall/Adelsberger":
		// 	dataLeft = {
		// 		name: "Eva Krall",
		// 		addition: lang === "de" ? "Assistenz der Geschäftsführung" : "Management Assistant"
		// 	};
		// 	dataRight = {
		// 		name: "Evelyn Adelsberger",
		// 		addition: "Marketing"
		// 	};
		// 	type = "duo";
		// 	image = "krall-adelsberger";
		// 	break;
		// case "Edinger/Duncan":
		// 	dataLeft = {
		// 		name: "Isabella Edinger (Isi)",
		// 		addition: "Front Office"
		// 	};
		// 	dataRight = {
		// 		name: "Kathrin Duncan (Kathl)",
		// 		addition: lang === "de" ? "Reservierung" : "Reservation"
		// 	};
		// 	type = "duo";
		// 	image = "edinger-duncan";
		// 	break;
		case "Adelsberger":
			dataSingle = {
				name: "Evelyn Adelsberger",
				addition: "Marketing"
			};
			type = "single";
			image = "adelsberger";
			break;
		case "Tschurtschenthaler":
			dataSingle = {
				name: "Jennifer Tschurtschenthaler (Jenny)",
				addition: lang === "de" ? "Rezeption" : "Reception"
			};
			type = "single";
			image = "tschurtschenthaler";
			break;
		case "Ilic":
			dataSingle = {
				name: "Aleksander Ilic (Aleks)",
				addition: lang === "de" ? "Küche Kaiser’s Deli" : "Kitchen Kaiser's Deli"
			};
			type = "single";
			image = "ilic";
			break;
		// case "Krall":
		// 	dataSingle = {
		// 		name: "Eva Krall",
		// 		addition: lang === "de" ? "Assistenz der Geschäftsführung" : "Management Assistant"
		// 	};
		// 	type = "single";
		// 	image = "krall";
		// 	break;
		case "Duncan":
			dataSingle = {
				name: "Kathrin Duncan",
				addition: lang === "de" ? "Reservierung" : "Reservation"
			};
			type = "single";
			image = "duncan";
			break;
		case "Daunghathai":
			dataSingle = {
				name: "Hame Daunghathai (Eissy)",
				addition: "Kid’s Club"
			};
			type = "single";
			image = "daunghathai";
			break;
		case "Cardos":
			dataSingle = {
				name: "Gabriella Cardos (Gabi)",
				addition: "Housekeeping"
			};
			type = "single";
			image = "cardos";
			break;
		// case "Edinger":
		// 	dataSingle = {
		// 		name: "Isabella Edinger (Isi)",
		// 		addition: "Front Office"
		// 	};
		// 	type = "single";
		// 	image = "edinger";
		// 	break;
		case "Landlinger":
			dataSingle = {
				name: "Michael Landlinger-Weilbold",
				addition: lang === "de" ? "Junior Chef & Gastgeber" : "Junior Boss & Host"
			};
			type = "single";
			image = "landlinger";
			break;
		case "Haushund":
			dataSingle = {
				name: "Emma",
				addition: lang === "de" ? "Haushund" : "House dog"
			};
			type = "single";
			image = "haushund";
			break;

		default:
			dataLeft = {
				name: "Barbara Winkler",
				addition: lang === "de" ? "Gastgeberin" : "Hostess"
			};
			dataRight = {
				name: "Michael Landlinger-Weilbold",
				addition: lang === "de" ? "Junior Chef & Gastgeber" : "Junior Boss & Host"
			};
			type = "duo";
			image = "winkler-landlinger";
			break;
	}

	useEffect(() => {
		if (inView) {
			controls.start("visible");
		}
	}, [controls, inView]);

	const imageVariants = {
		hidden: { y: 100, opacity: 0 },
		visible: {
			y: 0,
			opacity: 1,
			transition: {
				delay: 0,
				duration: 1
			}
		}
	};

	const labelsVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				delay: 1,
				duration: 0.5,
				ease: "easeIn"
			}
		}
	};

	return (
		<div className={css.root}>
			<Typography
				align="center"
				className={css.headline}
				color="secondary"
				component="h2"
				variant="h3"
			>
				{l("footer.headlineTeam")}
			</Typography>
			<div className={clsx("mco-footer-02__team-wrapper", css.wrapper)}>
				{type === "duo" && (
					<div className={css.left}>
						<motion.div
							ref={ref}
							variants={labelsVariants}
							initial="hidden"
							animate={controls}
						>
							<Typography className={css.name} component="p" variant="body2">
								{dataLeft?.name}
							</Typography>
							<Typography className={css.position} component="span" variant="subtitle1">
								{dataLeft?.addition}
							</Typography>
						</motion.div>
					</div>
				)}
				<div className={clsx("mco-footer-02__image", css.image)}>
					<motion.div
						ref={ref}
						className="Box"
						initial="hidden"
						animate={controls}
						variants={imageVariants}
					>
						<picture>
							<source
								srcSet={`/img/team/${image}${!bigScreen ? "-mobile" : ""}.webp`}
								type="image/webp"
							></source>
							<source
								srcSet={`/img/team/${image}${!bigScreen ? "-mobile" : ""}.png`}
								type="image/png"
							></source>
							<img
								src={`/img/team/${image}${!bigScreen ? "-mobile" : ""}.png`}
								height={bigScreen ? 590 : 222}
								width={bigScreen ? 1242 : 373}
								alt="Team"
								loading="lazy"
							></img>
						</picture>
					</motion.div>
				</div>
				{type === "duo" && (
					<div className={css.right}>
						<motion.div
							ref={ref}
							variants={labelsVariants}
							initial="hidden"
							animate={controls}
						>
							<Typography className={css.name} component="p" variant="body2">
								{dataRight?.name}
							</Typography>
							<Typography className={css.position} component="span" variant="subtitle1">
								{dataRight?.addition}
							</Typography>
						</motion.div>
					</div>
				)}
				{type === "single" && (
					<div className={css.single}>
						<motion.div
							ref={ref}
							variants={labelsVariants}
							initial="hidden"
							animate={controls}
						>
							<Typography className={css.name} component="p" variant="body2">
								{dataSingle?.name}
							</Typography>
							<Typography className={css.position} component="span" variant="subtitle1">
								{dataSingle?.addition}
							</Typography>
						</motion.div>
					</div>
				)}
			</div>
		</div>
	);
};

Team.propTypes = {
	height: PropTypes.number,
	src: PropTypes.string,
	width: PropTypes.number
};

export default Team;
